import { PeriodComparisonDirection, formatDurationTrendChange, toPeriodComparisonListItems } from '@frontend/chart/components';
import { Chart } from '@frontend/chart/types';

export interface VendingProductPopularityItem {
    key: string;
    name: string;
    previous: number;
    current: number;
    percentChange: number | null;
    direction: PeriodComparisonDirection;
}

export const toVendingProductPopularityItems = (chart: Chart, unknownLabel: string): VendingProductPopularityItem[] => {
    const comparisonItems = toPeriodComparisonListItems(chart, unknownLabel);

    return comparisonItems.map((item) => {
        const series = chart.series.find((entry) => entry.name === item.key);
        const lastTwo = (series?.data ?? []).slice(-2);
        const previous = lastTwo.length >= 2 ? lastTwo[0].value : 0;
        const current = lastTwo.length > 0 ? lastTwo[lastTwo.length - 1].value : item.value;

        return {
            key: item.key,
            name: item.name,
            previous,
            current,
            percentChange: item.percentChange,
            direction: item.direction
        };
    });
};

export const takeTopVendingProductPopularityItem = (items: VendingProductPopularityItem[], direction: 'up' | 'down'): VendingProductPopularityItem | null => {
    const ranked = items
        .filter((item) => item.direction === direction && item.percentChange !== null)
        .sort((left, right) => (right.percentChange ?? 0) - (left.percentChange ?? 0));

    return ranked[0] ?? null;
};

const byPercentChangeDesc = (left: VendingProductPopularityItem, right: VendingProductPopularityItem) => (right.percentChange ?? 0) - (left.percentChange ?? 0);

/** Top N risers and fallers by percent change (largest magnitude first). */
export const takeVendingProductPopularityRisersAndFallers = (
    items: VendingProductPopularityItem[],
    limit = 4
): { risers: VendingProductPopularityItem[]; fallers: VendingProductPopularityItem[] } => ({
    risers: items
        .filter((item) => item.direction === 'up' && item.percentChange !== null)
        .sort(byPercentChangeDesc)
        .slice(0, limit),
    fallers: items
        .filter((item) => item.direction === 'down' && item.percentChange !== null)
        .sort(byPercentChangeDesc)
        .slice(0, limit)
});

/** Formats absolute volume delta with optional percent, e.g. `+42 (+15.0%)` or `-40 (-50.0%)`. */
export const formatVendingPopularityTrendChange = (item: VendingProductPopularityItem): string => {
    const delta = Math.round(item.current - item.previous);
    const percentChange = item.percentChange === null ? null : item.direction === 'down' ? -Math.abs(item.percentChange) : item.percentChange;

    return formatDurationTrendChange(delta, percentChange);
};
